// In order to keep core.module.ts as small as possible,
// router functionality is kept in this file.

// This is not done with other modules because it is conventient
// to have 'ng generate component' automatically add a component to
// the appropriate module by running it in that directory.

import { RouterModule, Routes } from '@angular/router';

import { IdlePreloadStrategy } from './idle-preload.strategy';
// import { QuicklinkStrategy } from 'ngx-quicklink';

import { UserAuditHistoryComponent } from 'app/administration/users/edit-user/user-audit-history/user-audit-history.component';
import { UserFacilitiesComponent } from 'app/administration/users/edit-user/user-facilities/user-facilities.component';
import { UserLoginHistoryComponent } from 'app/administration/users/edit-user/user-login-history/user-login-history.component';
import { UserModuleAccessComponent } from 'app/administration/users/edit-user/user-module-access/user-module-access.component';
import { UserMultiFactorAuthenticationComponent } from 'app/administration/users/edit-user/user-multi-factor-authentication/user-multi-factor-authentication.component';
import { UserNotificationsComponent } from 'app/administration/users/edit-user/user-notifications/user-notifications.component';
import { UserOrganizationsComponent } from 'app/administration/users/edit-user/user-organizations/user-organizations.component';
import { UserPharmacyComponent } from 'app/administration/users/edit-user/user-pharmacy/user-pharmacy.component';
import { UserRxConnectsSettingsComponent } from 'app/administration/users/edit-user/user-rxconnects-settings/user-rxconnects-settings.component';
import { AdminWidgetListComponent } from 'app/administration/users/edit-user/widgets/admin-widget-list/admin-widget-list.component';
import { EditWidgetComponent } from 'app/administration/users/edit-user/widgets/edit-widget/edit-widget.component';
import { UserWidgetsComponent } from 'app/administration/users/edit-user/widgets/user-widgets/user-widgets.component';
import { BaContactsComponent } from 'app/ba/admin/contacts/contacts.component';
import { CiInvoicePdfComponent } from 'app/ci-repack/ci-invoices/invoice-pdf/invoice-pdf.component';
import { CiRepackOrderShortOverPrintableComponent } from 'app/ci-repack/ci-repack-order/ci-repack-order-short-over/ci-repack-order-short-over-printable/ci-repack-order-short-over-printable.component';
import { SilkierHistoryComponent } from 'app/silkier/silkier-history/silkier-history.component';
import { GlobalAuditListComponent } from '../administration/global-audit-list/global-audit-list.component';
import { EditUserComponent } from '../administration/users/edit-user/edit-user.component';
import { EditUserModule } from '../administration/users/edit-user/edit-user.module';
import { BaProgramsListComponent } from '../ba/admin/ba-programs/ba-programs-list/ba-programs-list.component';
import { FiscalYearsListComponent } from '../ba/admin/fiscal-years/fiscal-years-list/fiscal-years-list.component';
import { BudgetListComponent } from '../ba/budget-tracking/budget-list/budget-list.component';
import { BudgetInvoiceListComponent } from '../ba/budget-tracking/edit-budget-tabs/budget-invoices/budget-invoice-list/budget-invoice-list.component';
import { BudgetPosListComponent } from '../ba/budget-tracking/edit-budget-tabs/budget-pos/budget-pos-list/budget-pos-list.component';
import { KnowledgeBaseDocViewComponent } from '../help/knowledge-base/knowledge-base-doc-view/knowledge-base-doc-view.component';
import { KnowledgeBaseComponent } from '../help/knowledge-base/knowledge-base.component';
import { BusinessAreaEnum } from './enums/generated/BusinessAreaEnum';
import { PermissionRole } from './enums/generated/PermissionRole';
import { AccessDeniedComponent } from './pages/access-denied/access-denied.component';
import { ChangePasswordComponent } from './pages/change-password/change-password.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { Error404Component } from './pages/error404/error404.component';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { HelpDeskTicketFeedbackComponent } from './pages/help-desk-ticket-feedback/help-desk-ticket-feedback.component';
import { PublicHelpTicketComponent } from './pages/help-ticket/help-ticket.component';
import { HerosApplicationComponent } from './pages/heros-application/heros-application.component';
import { HolidaysComponent } from './pages/holidays/holidays.component';
import { HomeDeliveryComponent } from './pages/home-delivery/home-delivery.component';
import { InsulinApplicationComponent } from './pages/insulin-application/insulin-application.component';
import { HomeComponent } from './pages/home/home.component';
import { HomerRegisterComponent } from "./pages/homer-register/homer-register.component";
import { IcqFeedbackComponent } from './pages/icq-feedback/icq-feedback.component';
import { IcqSchedulingComponent } from "./pages/icq-scheduling/icq-scheduling.component";
import { LoginComponent } from './pages/login/login.component';
import { MobileScanningComponent } from './pages/mobile-scanning/mobile-scanning.component';
import { MobileScanningGuard } from './pages/mobile-scanning/mobile-scanning.guard';
import { PfsRegisterComponent } from "./pages/pfs-register/pfs-register.component";
import { DonorRegistrationComponent } from './pages/rxconnects/donor/donor-registration.component';
import { PatientApplicationComponent } from './pages/rxconnects/patient-application/patient-application.component';
import { RepositoryRegisterComponent } from './pages/rxconnects/repository/repository-register.component';
import { TechSpecComponent } from './pages/tech-spec/tech-spec.component';
import { WebReleaseNotesComponent } from './pages/web-release-notes/web-release-notes.component';
import { accessDeniedRestoreGuard } from './security/access-denied.service';
import { AuthGuard } from './security/auth.guard';
import { fallThroughGuard } from './security/fall-through.guard';
import { SecurityService } from './security/security.service';

// This is the root of our navigation menu route definitions
const routes: Routes = [
    // #region standalone components
    {
        path: 'admin/users/global-audit-list'
        , component: GlobalAuditListComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
        , data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.Admin ]
					, permissionRoles: [ ...SecurityService.setMinRole(PermissionRole.Manager) ]
				}
			]
		}
    }
    , {
        path: 'ba/admin/ba-programs/list'
        , component: BaProgramsListComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
        , data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.BA ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.SystemAdmin)
				}
			]
		}
    }
    , {
        path: 'ba/admin/contacts/list'
        , component: BaContactsComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
        , data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.BA ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.SystemAdmin)
				}
			]
		}
    }
    , {
        path: 'ba/admin/fiscal-years'
        , component: FiscalYearsListComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
        , data: {
            moduleAccess: [
                {
                    modules: [ BusinessAreaEnum.BA ]
                    , permissionRoles: SecurityService.setMinRole(PermissionRole.SystemAdmin)
                }
            ]
        }
    }
    , {
        path: 'ba/budgets/list'
        , component: BudgetListComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
        , data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.BA ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.User)
				}
			]
		}
    }
    , {
        path: 'ba/budgets/pos/list/:id'
        , component: BudgetPosListComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
        , data: {
            moduleAccess: [
                {
                    modules: [ BusinessAreaEnum.BA ]
                    , permissionRoles: SecurityService.setMinRole(PermissionRole.Manager)
                }
            ]
        }
    }
    , {
        path: 'ba/budgets/invoices/list/:id/:poId'
        , component: BudgetInvoiceListComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
        , data: {
            moduleAccess: [
                {
                    modules: [ BusinessAreaEnum.BA ]
                    , permissionRoles: SecurityService.setMinRole(PermissionRole.Manager)
                }
            ]
        }
    }
    , {
        path: 'ba/budgets/invoices/list/:id'
        , component: BudgetInvoiceListComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
        , data: {
            moduleAccess: [
                {
                    modules: [ BusinessAreaEnum.BA ]
                    , permissionRoles: SecurityService.setMinRole(PermissionRole.Manager)
                }
            ]
        }
    }
    // #endregion
    , {
		path: ''
		, component: HomeComponent
		, pathMatch: 'full'
	}
	, {
		path: 'dashboard'
        , component: DashboardComponent
		, canActivate: [ AuthGuard, MobileScanningGuard ]
	}
	, {
		path: 'login'
		, component: LoginComponent
	}
	// Where a signed-in user lands on a page they may not open. Guards and the http interceptor send them here
	// with the attempted URL kept in the address bar; see AccessDeniedService.
	, {
		path: 'access-denied'
		, component: AccessDeniedComponent
		, canActivate: [ accessDeniedRestoreGuard ]
	}
	, {
		path: 'pfs-register'
		, component: PfsRegisterComponent
	}
	, {
		path: 'homer-register'
		, component: HomerRegisterComponent
	}
	, {
		path: 'help-ticket'
		, component: PublicHelpTicketComponent
	}
	, {
		path: 'support/tickets/feedback/:id/:guid'
		, component: HelpDeskTicketFeedbackComponent
	}
	, {
		path: 'heros-application'
		, component: HerosApplicationComponent
	}
	, {
		path: 'home-delivery'
		, component: HomeDeliveryComponent
	}
	// Anonymous by design: an applicant has no HOMER account. No guard, and the app shell hides its
	// navigation for a signed-out visitor, so the page renders on its own.
	, {
		path: 'insulin/apply'
		, component: InsulinApplicationComponent
	}
	, {
		path: 'support/web-release-notes'
		, component: WebReleaseNotesComponent
		, canActivate: [ AuthGuard, MobileScanningGuard ]
	}
	, {
		path: 'holidays'
		, component: HolidaysComponent
	}
	, {
		path: 'tech-spec'
		, component: TechSpecComponent
		, canActivate: [ AuthGuard, MobileScanningGuard ]
	}
	, {
		path: 'admin/users/user/edit/details/:id'
		, component: EditUserComponent
		, canActivate: [ AuthGuard, MobileScanningGuard ]
	}
    , {
        path: 'admin/users/user/edit/organizations/:id'
        , component: UserOrganizationsComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
    }
    , {
        path: 'admin/users/user/edit/heros-agencies/:id'
        , component: UserFacilitiesComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
    }
    , {
        path: 'admin/users/user/edit/security/:id'
        , component: UserMultiFactorAuthenticationComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
    }
    , {
        path: 'admin/users/user/edit/notifications/:id'
        , component: UserNotificationsComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
    }
    , {
        path: 'admin/users/user/edit/notifications/:id/:el'
        , component: UserNotificationsComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
    }
    , {
        path: 'admin/users/user/edit/notifications/:id/:el/:reportId'
        , component: UserNotificationsComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
    }
    , {
        path: 'admin/users/user/edit/module-access/:id'
        , component: UserModuleAccessComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
    }
    , {
        path: 'admin/users/user/edit/pharmacy/:id'
        , component: UserPharmacyComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
        , data: {
            moduleAccess: [
                {
                    modules: [ BusinessAreaEnum.Inventory ]
                    , permissionRoles: SecurityService.setMinRole(PermissionRole.User)
                }
            ]
        }
    }
    , {
        path: 'admin/users/user/edit/widgets/:id'
        , component: UserWidgetsComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
    }
    , {
        path: 'admin/system/widgets'
        , component: AdminWidgetListComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
        , data: {
            moduleAccess: [
                {
                    modules: [ BusinessAreaEnum.Admin ]
                    , permissionRoles: SecurityService.setMinRole(PermissionRole.SystemAdmin)
                }
            ]
        }
    }
    , {
        path: 'admin/system/widgets/edit/:widgetId'
        , component: EditWidgetComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
        , data: {
            moduleAccess: [
                {
                    modules: [ BusinessAreaEnum.Admin ]
                    , permissionRoles: SecurityService.setMinRole(PermissionRole.SystemAdmin)
                }
            ]
        }
    }
    , {
        path: 'admin/users/user/edit/login-history/:id'
        , component: UserLoginHistoryComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
    }
    , {
        path: 'admin/users/user/edit/audit-history/:id'
        , component: UserAuditHistoryComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
    }
    , {
        path: 'admin/users/user/edit/rx-connects/:id'
        , component: UserRxConnectsSettingsComponent
        , canActivate: [ AuthGuard, MobileScanningGuard ]
    }
    , {
		path: 'forgot-password'
		, component: ForgotPasswordComponent
	}
	, {
		path: 'change-password'
		, component: ChangePasswordComponent
	}
	// IMPORTANT: This will be the path for ur CI Invoice email, accessible by the public API.
	, {
		path: 'public/invoice/pdf/:code'
		, component: CiInvoicePdfComponent
	}
	, {
		path: 'admin/system/quartz/pcg-history'
		, component: SilkierHistoryComponent
		, canActivate: [ AuthGuard, MobileScanningGuard ]
	}
	, {
		path: 'ci-short-over'
		, component: CiRepackOrderShortOverPrintableComponent
	}
	, {
		path: 'support/knowledge-base'
		, canActivate: [ AuthGuard, MobileScanningGuard ]
		, component: KnowledgeBaseComponent
		, data: {
			moduleAccess: [
				{
					modules: SecurityService.anyArea()
					, permissionRoles: SecurityService.setMinRole(PermissionRole.User)
				}
			]
		}
	}
	, {
		path: 'repositoryregister'
		, component: RepositoryRegisterComponent
	}
	, {
		path: 'donorregistration'
		, component: DonorRegistrationComponent
	}
	, {
		path: 'patient-application'
		, component: PatientApplicationComponent
	}
	, {
		path: 'patient-application/:id'
		, component: PatientApplicationComponent
		, canActivate: [ AuthGuard, MobileScanningGuard ]
		, data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.RxConnects ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.Pharmacist)
				}
			]
		}
	}
	, {
		path: 'support'
		, canLoad: [ AuthGuard, MobileScanningGuard ]
		, loadChildren: () => import('../help-desk/help-desk.module').then(m => m.HelpDeskModule)
	}
	, {
		path: 'error-report'
		, canLoad: [ AuthGuard, MobileScanningGuard ]
		, loadChildren: () => import('../error-report/error-report.module').then(m => m.ErrorReportModule)
	}
	, {
		path: 'admin'
		, canLoad: [ AuthGuard, MobileScanningGuard ]
		, loadChildren: () => import('../administration/administration.module').then(m => m.AdministrationModule)
		, data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.Admin ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.User)
				}
			]
		}
	}
	, {
		path: 'pcg'
		, canLoad: [ AuthGuard, MobileScanningGuard ]
		, loadChildren: () => import('../administration/pcg.module').then(m => m.PcgModule)
		, data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.Admin ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.User)
				}
			]
		}
	}
	, {
		path: 'heros'
		, canLoad: [ AuthGuard, MobileScanningGuard ]
		, data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.HEROS ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.User)
				}
			]
		}
		, loadChildren: () => import('../heros/heros.module').then(m => m.HEROSModule)
	}	
	, {
		path: 'ndcs'
		, canLoad: [ AuthGuard, MobileScanningGuard ]
		, data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.NDC ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.User)
				}
			]
		}
		, loadChildren: () => import('../redbook/redbook.module').then(m => m.RedbookModule)
	}
	, {
		path: 'ndcs'
		, canActivate: [ AuthGuard, MobileScanningGuard ]
		, data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.NDC ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.User)
				}
			]
		}
		, loadChildren: () => import('../ndc-directory/ndc-directory.module').then(m => m.NdcDirectoryModule)
	}
	// The Central Pharmacy queue of finalized Insulin/Epilepsy application PDFs. A full path ahead of
	// the lazy 'pharmacy' modules below so it is matched before any of them is loaded to look. Gated on
	// the Pharmacy module like the rest; the server enforces the same on every endpoint.
	, {
		path: 'pharmacy/medication-assistance/forms'
		, canMatch: [ AuthGuard, MobileScanningGuard ]
		, data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.Inventory ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.User)
				}
			]
		}
		, loadComponent: () => import('../pharmacy/medication-assistance-forms/medication-assistance-forms.component')
			.then(m => m.MedicationAssistanceFormsComponent)
	}
	, {
		path: 'pharmacy'
		, canMatch: [ AuthGuard, MobileScanningGuard ]
		, loadChildren: () => import('../reports/pharmacy/reportsPharmacy.module').then(m => m.ReportsPharmacyModule)
	}
	, {
		path: 'pharmacy'
		, canMatch: [ AuthGuard, MobileScanningGuard ]
		, data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.Inventory ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.Pharmacist)
				}
			]
		}
		, loadChildren: () => import('../pharm-log/pharm-log.module').then(m => m.PharmLogModule)
	}
	, {
		path: 'pharmacy'
		, canMatch: [ AuthGuard, MobileScanningGuard ]
		, data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.Inventory ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.User)
				}
			]
		}
		, loadChildren: () => import('../fulfillment/fulfillment.module').then(m => m.FulfillmentModule)
	}
	, {
		path: 'pharmacy'
		, canMatch: [ AuthGuard, MobileScanningGuard ]
		, data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.Inventory ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.User)
				}
			]
		}
		, loadChildren: () => import('../inventory/inventory.module').then(m => m.InventoryModule)
	}	
	// Last stop for a pharmacy URL that none of the alternatives above took: a role one of them refused gets Access
	// Denied, instead of falling through to '**' and landing on the dashboard with no explanation.
	, {
		path: 'pharmacy'
		, children: [ { path: '**', canActivate: [ fallThroughGuard('pharmacy') ], children: [] } ]
	}
	, {
		path: 'reporting'
		, canLoad: [ AuthGuard, MobileScanningGuard ]
		, loadChildren: () => import('../reports/reporting.module').then(m => m.ReportingModule)
	}
	, {
		path: 'metabolic-formula'
		, canLoad: [ AuthGuard, MobileScanningGuard ]
		, loadChildren: () => import('../metabolic-formula/metabolic-formula.module').then(m => m.MetabolicFormulaModule)
	}
	, {
		path: 'ci-repack'
		, canLoad: [ AuthGuard, MobileScanningGuard ]
		, loadChildren: () => import('../ci-repack/ci-repack.module').then(m => m.CiRepackModule)
	}
	, {
		path: 'icq'
		, canLoad: [ AuthGuard, MobileScanningGuard ]
		, loadChildren: () => import('../icq/icq.module').then(m => m.IcqModule)
	}
	, {
		path: 'insulin-program'
		, canLoad: [ AuthGuard, MobileScanningGuard ]
		, data: {
			moduleAccess: [
				{
					modules: [ BusinessAreaEnum.InsulinProgram ]
					, permissionRoles: SecurityService.setMinRole(PermissionRole.User)
				}
			]
		}
		, loadChildren: () => import('../insulin-program/insulin-program.module').then(m => m.InsulinProgramModule)
	}
	, {
		path: 'shared'
		, canActivate: [ AuthGuard, MobileScanningGuard ]
		, canLoad: [ AuthGuard, MobileScanningGuard ]
		, loadChildren: () => import('../shared/shared.module').then(m => m.SharedModule)
	}
	, {
		path: 'rxconnects'
        , data: { preload: false }
		, loadChildren: () => import('../rxconnects/rxconnects.module').then(m => m.RxConnectsModule)
	}
	, {
		path: 'inventories'
		, loadChildren: () => import("../shared/facilities/facility-inventories/inventories.module").then(m => m.InventoriesModule)
	}
	, {
		path: 'inv'
		, loadChildren: () => import("../formulary-inventory/formulary-inventory.module").then(m => m.FormularyInventoryModule)
	}
	, {
		path: 'ba'
		, loadChildren: () => import("../ba/ba.module").then(m => m.BAModule)
	}
	, {
		path: 'icq/scheduling/:icqReviewId/:userId'
		, component: IcqSchedulingComponent
	}
    , {
        path: 'icq-feedback/:id/:guid'
        , component: IcqFeedbackComponent
    }
    , {
        path: 'mobile-scanning'
        , component: MobileScanningComponent
        , canLoad: [ AuthGuard, MobileScanningGuard ]
        , canActivate: [ AuthGuard, MobileScanningGuard ]
    }
	, {
		path: '**'
		, redirectTo: ''
	}
];

export class CoreRouting {
	static readonly declarations = [
		HomeComponent
		, LoginComponent
		, HolidaysComponent
		, Error404Component
		, ForgotPasswordComponent
		, ChangePasswordComponent
		, KnowledgeBaseComponent
		, KnowledgeBaseDocViewComponent
		, PfsRegisterComponent
		, HomerRegisterComponent
		, PublicHelpTicketComponent
		, RepositoryRegisterComponent
		, CiInvoicePdfComponent
		, CiRepackOrderShortOverPrintableComponent
	];
	static readonly imports = [
		RouterModule.forRoot(
			routes
			, {
				scrollPositionRestoration: 'enabled'
				// Users were reporting 'dead clicks' when clicking on nav items. This issue was partially caused by the chunk loading strategy.
                // QuicklinkStrategy only loads chunks visible in the viewport, which excludes nested nav items. If a user clicks a nested nav item, the 
                // needed chunks are download on demand. This download can take a few seconds, making the link appear 'dead'. 

                // IdlePreloadStrategy preloads all chunks in the background while the user/browser is idle. 
                // If this strategy causes problems, switch it back to QuicklinkStrategy
                , preloadingStrategy: IdlePreloadStrategy
                // , preloadingStrategy: QuicklinkStrategy
				, anchorScrolling: 'enabled'
			}
		)
		, EditUserModule
		, SilkierHistoryComponent
	];
	static readonly exports = [ RouterModule ];
	static readonly providers = [ AuthGuard, MobileScanningGuard ];
}